import Vue from 'vue';
import { defineStore } from 'pinia';

export const useInsightsStore = defineStore('insights', {
  state: () => ({
    aiInsights: {},
    aiSigners: [],
    isAISignersReady: false,
    isAISignersProcessed: false,
    aiSignersLoadText: 'Importing AI suggested signers...',
    aiFields: [],
    isAIFieldsReady: false,
    aiTitles: [],
    isAITitlesReady: false,
    isAIFieldsRemoved: false,
    superDocSignedUrl: null,
    isProcessingStarted: false,
    insightsProgressVal: 10,
    skipInsightsImport: false,
    isAIInsightsFailed: false,
    abortController: null,
    url: null,
  }),
  actions: {
    toHoursAndMinutes(totalMinutes) {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return {
        hours,
        minutes,
      };
    },
    getNotificationTimeText() {
      const {
        hours,
        minutes,
      } = this.toHoursAndMinutes(Math.round(this.aiFields.length * 0.5));
      return `${hours > 0 ? `${hours} hour${hours === 1 ? '' : 's'} and ` : ''}${minutes} minutes`;
    },
    isActiveResults({url, draftId}) {
      if(url) {
        return  this.aiInsights?.url === url;
      }
      if(draftId) {
        return this.aiInsights?.draft_id === draftId;
      }
      return false;
    },

    processInsightsJSONResponse(json) {
      this.aiInsights = JSON.parse(json);
      if(!this.isAISignersReady && this.aiInsights.document_signers) {
        this.isAISignersReady = true;
        this.aiSigners = this.aiInsights.document_signers;
        console.log("Importing AI Signers.....");
      }
      if(this.aiInsights.document_title) {
        this.isAITitlesReady = true;
        this.aiTitles = this.aiInsights?.document_title;
        console.log("Importing AI Title.....");
      }
      if(this.aiInsights.is_fields_ready && !this.isAIFieldsReady) {
        this.isAIFieldsReady = true;
        this.aiFields = this.aiInsights.document_fields;
        console.log("Importing AI Fields.....");
      }

    },

    async getAiInsights({fileInfo, draftId}) {
      this.abortController = new AbortController();
      if(this.isProcessingStarted) return;
      this.isProcessingStarted = true;
      let options = {stream: true};
      if(fileInfo && fileInfo?.originalUrl) {
        options["url"] = fileInfo.originalUrl;
        this.url = fileInfo.originalUrl;
      }
      else if(draftId) {
        options["draft_id"] = draftId;
      }
      try {
        const response = await fetch('/api/insights', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          signal: this.abortController.signal,
          body: JSON.stringify(options)
        });

        if (!response.ok) {
          this.isAIInsightsFailed = true;
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        while (this.isProcessingStarted && reader) {
          const {value, done} = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, {stream: true});

          let lines = buffer.split("\n");
          buffer = lines.pop(); // save last partial line
          for (const line of lines) {
            if (line.trim()) {
              try {
                this.processInsightsJSONResponse(line);
              } catch (err) {
                this.isAIInsightsFailed = true;
                console.error("Invalid JSON:", line, err);
              }
            }
          }
        }
        if (buffer.trim()) {
          this.processInsightsJSONResponse(buffer);
        }
      } catch (err) {
        this.isAIInsightsFailed = true;
        this.isProcessingStarted = false;
      }
    },
    stopPreviousStreaming() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
      this.isProcessingStarted = false;
      this.aiInsights = {};
      this.aiSigners = [];
      this.isAISignersReady = false;
      this.isAISignersProcessed = false;
      this.aiFields = [];
      this.isAIFieldsReady = false;
      this.aiTitles = [];
      this.isAITitlesReady = false;
      this.isAIInsightsFailed = false;
      this.abortController = null;
      this.url = null;
    }
  },
});
